<template>
  <div v-show="displayFrontend" :class="{questions__save: safeFreeze}" class="questions">

    <h1 class="questions__header">{{ $t("Company structure") }}</h1>
    <p>{{
        $t("For how many and which branches will you use Dyflexis? Enter the branch names and their addresses below.")
      }}</p>

    <p v-show="showPayrollConnection">{{
        $t("Please note, you will be using a (payroll) connection. Branches play a major role in setting up the connection. The mapping is 1 on 1. This means one environment/employer/cost center is linked to one location in Dyflexis. In this way, the employees and hours for the remuneration are in the correct place.")
      }}</p>

    <b-form-textarea
      v-if="showOldOverview"
      id="textarea-auto-height"
      :disabled="readOnly"
      v-model="departments.businessUnits"
      class="questions__textarea"
      max-rows="20"
      rows="5"
    ></b-form-textarea>

    <div v-if="!showOldOverview">
      <div class="departements__left">
        <template v-for="(unit, index) in departments.businessUnits" v-if="index===showDepartment">
          <div>
            <h2 class="questions__subheader">
              {{ $t("Department name") }}
            </h2>
            <div>
              <input
                id="departmentName"
                v-model="unit.name"
                class="form-control"
                type="text"
              />
            </div>
          </div>

          <div v-if="legalEntityAmount>1&&showPayrollConnection">
            <h2 class="questions__subheader">
              {{ $t("Legal entity name of this department") }}
            </h2>
            <div>
              <input
                id="legalEntityName"
                v-model="unit.legalEntityName"
                class="form-control"
              />
            </div>
          </div>

          <h2 class="questions__subheader">
            {{ $t("Search the department address") }}
          </h2>
          <input
            id="autocomplete"
            :disabled="readOnly"
            class="form-control"
            @focus="searchAddress(index)"/>

          <div class="deliveryStreet">
            <h2 class="questions__subheader">
              {{ $t("Street") }}
            </h2>
            <input
              v-model="unit.street"
              :disabled="unit.name===null||readOnly||addressInputDisabled"
              class="form-control"/>
          </div>

          <div class="deliveryStreetNumber">
            <h2 class="questions__subheader">
              {{ $t("House number") }}
            </h2>
            <input
              v-model="unit.streetNumber"
              :disabled="unit.name===null||readOnly||addressInputDisabled"
              class="form-control"/>
          </div>

          <div class="deliveryStreetNumber">
            <h2 class="questions__subheader">
              {{ $t("Subpremise") }}
            </h2>
            <input
              v-model="unit.subPremise"
              :disabled="unit.name===null||readOnly||addressInputDisabled"
              class="form-control"/>
          </div>

          <div class="deliveryCity">
            <h2 class="questions__subheader">
              {{ $t("City") }}
            </h2>
            <input
              id="locality"
              v-model="unit.city"
              :disabled="unit.name===null||readOnly||addressInputDisabled"
              class="form-control"/>
          </div>

          <div class="deliveryPostalCode">
            <h2 class="questions__subheader">
              {{ $t("Zip code") }}
            </h2>
            <input
              id="postal_code"
              v-model="unit.postalCode"
              :disabled="unit.name===null||readOnly||addressInputDisabled"
              class="form-control"/>
          </div>

          <input
            id="country"
            v-model="unit.country"
            type="hidden"/>

          <b-button
            :disabled="readOnly"
            class="department__addRow question--marginTop"
            variant="success"
            @click="changeDepartment(index)">
            <b>+</b> {{ $t("Save department") }}
          </b-button>
        </template>
      </div>
      <div
        v-b-popover.hover.left="$t('You have signed for {departmentAmount} departments. Contact your consultant if you would like to implement Dyflexis in more departments.', {departmentAmount: this.departmentsAmount})"
        class="departments__right">
        <p v-show="departments.businessUnits[0].name !== undefined && departments.businessUnits[0].name !== null">
          <b>{{ $t("Departments") }}</b></p>
        <template v-for="(unit, index) in departments.businessUnits">
          <p class="cursor__pointer" @click="showDepartment=index;addressInputDisabled=false;">{{ unit.name }}</p>
        </template>

        <b-button
          v-show="departments.businessUnits[0].name !== null && departmentsAmount > 1"
          :disabled="readOnly || (departmentsAmount < departments.businessUnits.length)"
          class="department__addRow"
          variant="success"
          @click="newDepartment(index)">
          <b>+</b> {{ $t("New department") }}
        </b-button>
      </div>
    </div>

    <h1 class="question--marginTop questions__header">{{ $t("Department structure") }}</h1>
    <p>{{
        $t("Employees can be scheduled to specific departments. You determine the levels of these departments. The actual schedule is created in the lowest departments in the structure. Each branch has its own departmental structure.")
      }}</p>

    <b>{{ $t("A good department structure:") }}</b>
    <ul>
      <li>{{ $t("Contains a reference so the employee knows what to do or where to be") }}</li>
      <li>{{
          $t("Assists the scheduler in verifying staffing levels and whether every employee required is on the schedule")
        }}
      </li>
      <li>{{ $t("Helps to distinguish employees with specific competencies") }}</li>
      <li>{{
          $t("Supports the usage of employee budgets and reports where branches can be compared with each other, without getting in the way of accurate scheduling")
        }}
      </li>
    </ul>

    <p class="question__paragraph-margin">
      <i><b>{{ $t("Example:") }}</b><br>
      {{ $t("Branch") }} A<br>
          > {{ $t("Main Department") }} 1<br>
          <span class="question__subdepartment">>> {{ $t("Department") }} 1</span><br>
          <span class="question__subdepartment">>> {{ $t("Department") }} 2</span><br>
          > {{ $t("Main Department") }} 2</i>
    </p>

    <p>{{ $t("Add the department structure for each branch below") }}</p>
    <b-form-textarea
      v-if="showOldOverview"
      id="textarea-auto-height"
      v-model="departments.departmentsDescription"
      :disabled="readOnly"
      class="questions__textarea"
      max-rows="20"
      rows="5"
    ></b-form-textarea>

    <div v-show="!showOldOverview">
      <b-form-select :disabled="businessUnitDropdownDisabled" v-model="selectedDepartment">
        <b-form-select-option disabled value="null">{{ $t('Select branch') }}</b-form-select-option>
        <template v-for="(unit, index) in departments.businessUnits" v-if="unit.name !== null">
          <b-form-select-option :value="unit.name">{{ unit.name }}</b-form-select-option>
        </template>
      </b-form-select>

      <template v-for="(unit, index) in departments.businessUnits" v-if="unit.name===selectedDepartment">
        <b-form-textarea
          id="textarea-auto-height"
          v-model="unit.structure"
          :disabled="readOnly"
          class="questions__textarea question__paragraph-margin"
          max-rows="20"
          rows="8"
        ></b-form-textarea>
      </template>
    </div>


    <div v-show="departmentsAmount > 1">
      <h1 class="question--marginTop questions__header">{{ $t("Secondment") }}</h1>
      <p>{{
          $t("Employees can be scheduled in specific departments. The structure of these departments is up to you. The actual schedule is created in the lowest departments in the structure. Each branch has its own departmental structure.")
        }}</p>
      <b-form-checkbox
        v-model="departments.departmentSecondment"
        class="checkbox"
        name="check-button"
        switch>
        {{ $t("Secondment is possible") }}
      </b-form-checkbox>
    </div>

    <input :value="$t('Save')" type="button" @click="handleSave"/>
    <p v-show="showSaveStatus" class="questions--savesuccess">{{ $t("Saved successfully!") }}</p>
  </div>
</template>

<script>
import SystemRequestService from "@/SystemRequestService";
import GlobalFunctions from "@/GlobalFunctions";

export default {
  name: 'Departments',
  data() {
    return {
      showSaveStatus: false,
      displayFrontend: false,
      safeFreeze: false,
      readOnly: false,
      showPayrollConnection: false,
      departmentsAmount: 1,
      legalEntityAmount: 1,
      businessUnitDropdownDisabled: true,
      showAdditionalHours: false,
      showConsignmentShift: false,
      selectedDepartment: null,
      showOldOverview: false,//backwards compatible
      showDepartment: 0,
      addressInputDisabled: true,
      departments: {
        businessUnits: [{
          name: null,
          legalEntityName: null,
          street: null,
          streetNumber: null,
          subPremise: null,
          city: null,
          postalCode: null,
          country: null,
          structure: null,
        }],
        departmentsDescription: [],
        departmentSecondment: null,
        shiftTimes: null
      },
    };
  },
  async beforeMount() {
    if (document.getElementById('mapsApi') === null) {
      const head = document.getElementsByTagName('head')[0];
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyCuJky5bTKvL7nGSTb4QUtAMfqJRZVEgxQ&'
        + 'libraries=places';
      script.id = 'mapsApi';
      head.appendChild(script);
    }

    const result = await SystemRequestService.getProject(this.$route.params.hashedId);
    if (!result) {
      alert(this.$t("Error while obtaining your details. Please try again later or contact your Dyflexis consultant."));
      return;
    }

    this.showPayrollConnection = result.payrollConnection;
    this.departments.departmentsDescription = result.systemDepartments;
    this.departments.departmentSecondment = result.systemDepartmentSecondment;
    this.departments.shiftTimes = result.shiftTimes;
    this.departmentsAmount = result.departmentsAmount;
    this.legalEntitiesAmount = result.legalEntitiesAmount;
    this.selectedDepartment = this.departments.businessUnits[0].name;

    try {
      this.departments.businessUnits = JSON.parse(result.systemBusinessUnits.replaceAll("&quot;", '"').replaceAll("&gt;", '>'));

      if (this.departments.businessUnits[0].name == undefined) {
        throw 'JSON not correct';
      }

    } catch (error) {
      this.departments.businessUnits = [{
        name: null,
        legalEntityName: null,
        street: null,
        streetNumber: null,
        subPremise: null,
        city: null,
        postalCode: null,
        country: null,
        structure: null,
      }];

      if (result.systemDepartments != null || result.systemBusinessUnits != null) {
        this.showOldOverview = true;
        this.departments.departmentsDescription = this.removeHtml(result.systemDepartments);
        this.departments.businessUnits = this.removeHtml(result.systemBusinessUnits);
      }
    }

    if (this.showOldOverview && this.departments.businessUnits === null) {

    }

    this.readOnly = result.isSigned;
    GlobalFunctions.setReadOnly(result.isSigned);
    this.businessUnitDropdownDisabled = false;

    localStorage.setItem('startingObject', JSON.stringify(this.departments));
    this.displayFrontend = true;
  },
  beforeRouteLeave: function (to, from, next) {
    if (!GlobalFunctions.acceptPageLeave(this.departments, this.$t("You have made changes without saving them. Press cancel to ignore these changes and leave the page."))) {
      this.handleSave(false);
    }
    next();
  },
  computed: {
    numberApproved() {
      return this.departments.shiftTimes < 5
        && this.departments.shiftTimes >= 0
        && this.departments.shiftTimes != null
        && this.departments.shiftTimes !== '';
    }
  },
  methods: {
    async handleSave(saveInitialObject) {
      this.safeFreeze = true;
      const result = await SystemRequestService.updateProject(this.departments, this.$route.params.hashedId);
      if (!result || result.status !== 'success') {
        alert(this.$t("Error while updating, please send a screenshot of the fields on this page to your Dyflexis consultant!"));
        return;
      }

      if (saveInitialObject !== false) {
        localStorage.setItem('startingObject', JSON.stringify(this.departments));
      }

      this.safeFreeze = false;
      this.showSaveStatus = true;
      setTimeout(() => {
        this.showSaveStatus = false;
      }, 3000);
    },
    removeHtml(text) {
      if (text == null) {
        return text;
      }

      let tmp = document.createElement("DIV");
      tmp.innerHTML = text.replaceAll("</p><p>", "\n").replaceAll("<br>", "\n");
      return tmp.textContent || tmp.innerText || "";
    },
    searchAddress() {
      let autocomplete = new google.maps.places.Autocomplete(document.getElementById('autocomplete'), {types: []});
      autocomplete.setFields(['address_component']);
      autocomplete.addListener('place_changed', () => {
        let place = autocomplete.getPlace().address_components;
        if (place === undefined) {
          return;
        }

        if (!this.readOnly) {
          this.addressInputDisabled = false;
          this.departments.businessUnits[this.showDepartment].street = null;
          this.departments.businessUnits[this.showDepartment].streetNumber = null;
          this.departments.businessUnits[this.showDepartment].subPremise = null;
          this.departments.businessUnits[this.showDepartment].city = null;
          this.departments.businessUnits[this.showDepartment].postalCode = null;
          this.departments.businessUnits[this.showDepartment].country = null;
        }

        let index = this.showDepartment;
        this.departments.businessUnits[index].subPremise = null;
        for (let i = 0; i < place.length; i++) {
          let addressType = place[i].types[0];
          let val = place[i].long_name;

          if (addressType === 'route') {
            this.departments.businessUnits[index].street = val;
          }

          if (addressType === "street_number") {
            this.departments.businessUnits[index].streetNumber = val;
          }

          if (addressType === "subpremise") {
            this.departments.businessUnits[index].subPremise = val;
          }

          if (addressType === "locality") {
            this.departments.businessUnits[index].city = val;
          }

          if (addressType === "postal_code") {
            this.departments.businessUnits[index].postalCode = val;
          }

          if (addressType === "country") {
            this.departments.businessUnits[index].country = val;
          }
        }

        this.departments.businessUnits[index].name = this.departments.businessUnits[index].city;

        document.getElementById('autocomplete').value = '';
        document.getElementById('departmentName').focus();
      });
    },
    changeDepartment(index) {
    },
    newDepartment(index) {
      this.departments.businessUnits.push({
        name: null,
        legalEntityName: null,
        street: null,
        streetNumber: null,
        subPremise: null,
        city: null,
        postalCode: null,
        country: null,
        structure: null,
      });

      this.addressInputDisabled = true;
      this.showDepartment = this.departments.businessUnits.length - 1;
      this.$forceUpdate();
    },
  },
};
</script>
